import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { ChatPanelMessage } from '../../../models/chat-panel-message';

/**
 * OnPush isolates the message list from composer keystrokes in the parent.
 */
@Component({
  selector: 'app-chat-panel-messages',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  styleUrl: './chat-panel-messages.component.css',
  template: `
    <div #scrollEl class="d2-chat-messages">
      @if (hasMore) {
        <button
          type="button"
          class="d2-chat-load-more"
          (click)="loadMore.emit()"
          [disabled]="loadingMore"
        >
          @if (loadingMore) {
            Loading earlier messages…
          } @else {
            Load more messages
          }
        </button>
      }
      @if (historyLoading && !messages.length) {
        <p class="d2-chat-history-loading">Loading conversation…</p>
      }
      @for (m of messages; track trackMessage($index, m)) {
        <div
          class="d2-chat-message"
          [class.d2-chat-message--user]="m.role === 'user'"
          [class.d2-chat-message--assistant]="m.role === 'assistant'"
        >
          @if (m.role === 'assistant') {
            <img
              src="assets/AI.svg"
              alt=""
              class="d2-chat-ai-icon"
              width="28"
              height="28"
              aria-hidden="true"
            />
          }
          <div
            class="d2-chat-bubble"
            [class.d2-chat-bubble--user]="m.role === 'user'"
            [class.d2-chat-bubble--assistant]="m.role === 'assistant'"
            [class.d2-chat-bubble--loading]="m.loading"
          >
            @if (m.loading) {
              <span class="d2-chat-loading" aria-live="polite" aria-busy="true">
                <span class="d2-chat-loading-text">{{ m.text }}</span>
                <span class="d2-chat-loading-dots" aria-hidden="true">
                  <span></span>
                  <span></span>
                  <span></span>
                </span>
              </span>
            } @else if (m.htmlCache) {
              <div class="d2-chat-bubble-text" [innerHTML]="m.htmlCache"></div>
            }
          </div>
        </div>
      }
    </div>
  `,
})
export class ChatPanelMessagesComponent {
  @Input({ required: true }) messages: ChatPanelMessage[] = [];
  @Input() hasMore = false;
  @Input() loadingMore = false;
  @Input() historyLoading = false;

  @ViewChild('scrollEl') private scrollEl?: ElementRef<HTMLElement>;

  @Output() loadMore = new EventEmitter<void>();

  trackMessage(index: number, message: ChatPanelMessage): string {
    if (message.id) {
      return message.id;
    }
    if (message.loading) {
      return `loading-${index}`;
    }
    return `${message.role}-${message.createdAt ?? ''}-${index}`;
  }

  getScrollHost(): HTMLElement | undefined {
    return this.scrollEl?.nativeElement;
  }

  scrollToBottom(): void {
    queueMicrotask(() => {
      requestAnimationFrame(() => {
        const el = this.scrollEl?.nativeElement;
        if (!el) {
          return;
        }
        const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches
          ? 'auto'
          : 'smooth';
        el.scrollTo({ top: el.scrollHeight, behavior });
      });
    });
  }
}
